// Phase-1 supplier add/edit form — dynamic credential fields + custom k/v.
// Reused inside the Travel Partners dashboard; later phases (Payment Gateways)
// will mount the same component with a different `modules` whitelist.
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api, formatApiErrorDetail } from '../../lib/api';

const MODULES = [
  { key: 'flights',   label: 'Flights' },
  { key: 'hotels',    label: 'Hotels' },
  { key: 'trains',    label: 'Trains' },
  { key: 'buses',     label: 'Buses' },
  { key: 'cabs',      label: 'Cabs' },
  { key: 'villas',    label: 'Villas & Homestays' },
  { key: 'packages',  label: 'Holiday Packages' },
  { key: 'tours',     label: 'Tours & Activities' },
  { key: 'visa',      label: 'Visa Services' },
  { key: 'cruise',    label: 'Cruises' },
  { key: 'forex',     label: 'Forex' },
  { key: 'insurance', label: 'Insurance' },
];

const empty = {
  name: '',
  display_name: '',
  modules: [],
  environment: 'sandbox',
  status: 'Draft',
  test_mode: true,
  logo_url: '',
  credentials: {
    api_url: '', username: '', password: '', api_key: '', secret_key: '',
    access_token: '', client_id: '', client_secret: '', merchant_id: '',
    rapidapi_host: '', rapidapi_key: '',
  },
  settings: {
    country: 'IN', currency: 'INR', timeout_seconds: 20, priority: 50,
    auto_sync: true, markup_percent: 0, commission_percent: 0, gst_percent: 0,
    cancellation_policy: '', notes: '',
  },
  customFields: [], // [{key, value}]
};

const labelCls = 'block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1';
const inputCls = 'w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500';

const Field = ({ label, children }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

const SupplierForm = ({ initialPartner, onClose, onSaved }) => {
  const isEdit = !!initialPartner;
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!initialPartner) { setForm(empty); return; }
    // Pull custom fields out of credentials.custom__*
    const creds = { ...initialPartner.credentials };
    const customFields = [];
    Object.keys(creds).forEach((k) => {
      if (k.startsWith('custom__')) {
        customFields.push({ key: k.replace('custom__', ''), value: creds[k] || '' });
        delete creds[k];
      }
    });
    setForm({
      ...empty,
      ...initialPartner,
      credentials: { ...empty.credentials, ...creds },
      settings: { ...empty.settings, ...(initialPartner.settings || {}) },
      customFields,
    });
  }, [initialPartner]);

  const setField = (path, value) => {
    setForm((f) => {
      const [a, b] = path.split('.');
      if (b) return { ...f, [a]: { ...f[a], [b]: value } };
      return { ...f, [a]: value };
    });
  };

  const toggleModule = (mk) => {
    setForm((f) => ({
      ...f,
      modules: f.modules.includes(mk) ? f.modules.filter((m) => m !== mk) : [...f.modules, mk],
    }));
  };

  const addCustomField = () => setForm((f) => ({ ...f, customFields: [...f.customFields, { key: '', value: '' }] }));
  const updateCustom = (i, k, v) => setForm((f) => {
    const next = [...f.customFields];
    next[i] = { ...next[i], [k]: v };
    return { ...f, customFields: next };
  });
  const removeCustom = (i) => setForm((f) => ({ ...f, customFields: f.customFields.filter((_, idx) => idx !== i) }));

  const payload = useMemo(() => {
    const { customFields, ...rest } = form;
    return {
      ...rest,
      // Skip masked echoes — backend keeps untouched fields anyway
      credentials: {
        ...Object.fromEntries(Object.entries(rest.credentials || {}).filter(([, v]) => !(typeof v === 'string' && v.includes('•')))),
        custom: Object.fromEntries(customFields.filter((cf) => cf.key.trim()).map((cf) => [cf.key.trim(), cf.value])),
      },
    };
  }, [form]);

  const save = async (statusOverride) => {
    if (!form.name || !form.name.trim()) { toast.error('Supplier name is required'); return; }
    if (!form.modules.length) { toast.error('Select at least one module this supplier serves'); return; }
    setSaving(true);
    try {
      const body = { ...payload, status: statusOverride || form.status };
      let res;
      if (isEdit) res = await api.put(`/admin/partners/${initialPartner.id}`, body);
      else res = await api.post('/admin/partners', body);
      toast.success(isEdit ? 'Supplier updated' : `Supplier created (${body.status})`);
      onSaved && onSaved(res.data);
      onClose && onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    if (!isEdit) { toast.error('Save as Draft first, then test connection.'); return; }
    setTesting(true);
    try {
      const r = await api.post(`/admin/partners/${initialPartner.id}/test`, { method: 'GET' });
      if (r.data.ok) toast.success(`${r.data.state.toUpperCase()} · ${r.data.latency_ms}ms · HTTP ${r.data.http_status}`);
      else toast.error(`Connection failed: ${r.data.error || r.data.state}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="w-full max-w-4xl bg-white dark:bg-gray-900 rounded-xl shadow-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="supplier-form-modal">
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">{isEdit ? 'Edit Supplier' : 'Add New Supplier'}</h2>
            <p className="text-xs text-gray-500 mt-0.5">Configure API credentials and module assignments.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" data-testid="form-close-btn">×</button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic */}
          <section>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Basic Info</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Supplier Name *">
                <input data-testid="form-name" value={form.name} onChange={(e) => setField('name', e.target.value)} className={inputCls} placeholder="e.g. tbo_holidays" />
              </Field>
              <Field label="Display Name">
                <input data-testid="form-display-name" value={form.display_name} onChange={(e) => setField('display_name', e.target.value)} className={inputCls} placeholder="TBO Holidays" />
              </Field>
              <Field label="Environment">
                <select data-testid="form-environment" value={form.environment} onChange={(e) => setField('environment', e.target.value)} className={inputCls}>
                  <option value="sandbox">Sandbox</option>
                  <option value="production">Production</option>
                </select>
              </Field>
              <Field label="Status">
                <select data-testid="form-status" value={form.status} onChange={(e) => setField('status', e.target.value)} className={inputCls}>
                  <option value="Draft">Draft</option>
                  <option value="Pending">Pending</option>
                  <option value="Active">Active</option>
                  <option value="Suspended">Suspended</option>
                </select>
              </Field>
              <Field label="Logo URL">
                <input data-testid="form-logo" value={form.logo_url} onChange={(e) => setField('logo_url', e.target.value)} className={inputCls} placeholder="https://…" />
              </Field>
              <Field label="Modules served *">
                <div className="flex flex-wrap gap-2">
                  {MODULES.map((m) => {
                    const on = form.modules.includes(m.key);
                    return (
                      <button
                        type="button"
                        key={m.key}
                        onClick={() => toggleModule(m.key)}
                        data-testid={`form-module-${m.key}`}
                        className={`px-3 py-1 rounded-full text-xs font-semibold border ${on ? 'bg-red-600 text-white border-red-600' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700'}`}
                      >{m.label}</button>
                    );
                  })}
                </div>
              </Field>
            </div>
          </section>

          {/* Credentials */}
          <section>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">API Credentials <span className="font-normal text-xs text-gray-500">· secrets are encrypted at rest</span></h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {['api_url', 'username', 'password', 'api_key', 'secret_key', 'access_token', 'client_id', 'client_secret', 'merchant_id', 'rapidapi_host', 'rapidapi_key'].map((k) => (
                <Field key={k} label={k.replace(/_/g, ' ')}>
                  <input
                    data-testid={`form-cred-${k}`}
                    type={(k.includes('password') || k.includes('secret') || k === 'api_key' || k === 'access_token' || k === 'rapidapi_key') ? 'password' : 'text'}
                    value={form.credentials[k] || ''}
                    onChange={(e) => setField(`credentials.${k}`, e.target.value)}
                    className={inputCls}
                    placeholder={k === 'api_url' ? 'https://api.supplier.com' : ''}
                  />
                </Field>
              ))}
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <p className={labelCls}>Custom Fields</p>
                <button type="button" onClick={addCustomField} className="text-xs font-semibold text-red-600 hover:text-red-700" data-testid="add-custom-btn">+ Add field</button>
              </div>
              {form.customFields.length === 0 && <p className="text-xs text-gray-500 italic">None — click + Add field to capture any supplier-specific value.</p>}
              <div className="space-y-2">
                {form.customFields.map((cf, i) => (
                  <div key={i} className="flex gap-2">
                    <input className={`${inputCls} flex-1`} placeholder="Key (e.g. agent_code)" value={cf.key} onChange={(e) => updateCustom(i, 'key', e.target.value)} data-testid={`custom-key-${i}`} />
                    <input className={`${inputCls} flex-1`} placeholder="Value" value={cf.value} onChange={(e) => updateCustom(i, 'value', e.target.value)} data-testid={`custom-value-${i}`} />
                    <button type="button" onClick={() => removeCustom(i)} className="text-red-600 hover:text-red-700 px-2" data-testid={`custom-remove-${i}`}>×</button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Settings */}
          <section>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Business Settings</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Country">
                <input className={inputCls} value={form.settings.country} onChange={(e) => setField('settings.country', e.target.value)} data-testid="form-country" />
              </Field>
              <Field label="Currency">
                <input className={inputCls} value={form.settings.currency} onChange={(e) => setField('settings.currency', e.target.value)} data-testid="form-currency" />
              </Field>
              <Field label="Timeout (sec)">
                <input className={inputCls} type="number" min={1} max={120} value={form.settings.timeout_seconds} onChange={(e) => setField('settings.timeout_seconds', parseInt(e.target.value, 10) || 20)} data-testid="form-timeout" />
              </Field>
              <Field label="Priority (1=highest, 100=lowest)">
                <input className={inputCls} type="number" min={1} max={100} value={form.settings.priority} onChange={(e) => setField('settings.priority', parseInt(e.target.value, 10) || 50)} data-testid="form-priority" />
              </Field>
              <Field label="Markup %">
                <input className={inputCls} type="number" min={0} max={100} step="0.1" value={form.settings.markup_percent} onChange={(e) => setField('settings.markup_percent', parseFloat(e.target.value) || 0)} data-testid="form-markup" />
              </Field>
              <Field label="Commission %">
                <input className={inputCls} type="number" min={0} max={100} step="0.1" value={form.settings.commission_percent} onChange={(e) => setField('settings.commission_percent', parseFloat(e.target.value) || 0)} data-testid="form-commission" />
              </Field>
              <Field label="GST %">
                <input className={inputCls} type="number" min={0} max={100} step="0.1" value={form.settings.gst_percent} onChange={(e) => setField('settings.gst_percent', parseFloat(e.target.value) || 0)} data-testid="form-gst" />
              </Field>
              <Field label="Auto-sync">
                <label className="inline-flex items-center gap-2 mt-2">
                  <input type="checkbox" checked={form.settings.auto_sync} onChange={(e) => setField('settings.auto_sync', e.target.checked)} className="rounded text-red-600" data-testid="form-autosync" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Enable scheduled sync</span>
                </label>
              </Field>
              <Field label="Test mode">
                <label className="inline-flex items-center gap-2 mt-2">
                  <input type="checkbox" checked={form.test_mode} onChange={(e) => setField('test_mode', e.target.checked)} className="rounded text-red-600" data-testid="form-testmode" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Sandbox mode</span>
                </label>
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <Field label="Cancellation Policy">
                <textarea className={`${inputCls} h-20`} value={form.settings.cancellation_policy} onChange={(e) => setField('settings.cancellation_policy', e.target.value)} placeholder="Free cancellation till 24h before…" data-testid="form-cancellation" />
              </Field>
              <Field label="Notes">
                <textarea className={`${inputCls} h-20`} value={form.settings.notes} onChange={(e) => setField('settings.notes', e.target.value)} placeholder="Internal notes…" data-testid="form-notes" />
              </Field>
            </div>
          </section>
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 px-6 py-3 flex flex-wrap items-center justify-between gap-3 rounded-b-xl">
          <button onClick={testConnection} disabled={!isEdit || testing} className="text-sm font-semibold text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-md px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50" data-testid="form-test-btn">
            {testing ? 'Testing…' : 'Test Connection'}
          </button>
          <div className="flex gap-2">
            <button onClick={() => save('Draft')} disabled={saving} className="text-sm font-semibold text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-md px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50" data-testid="form-save-draft-btn">
              Save as Draft
            </button>
            <button onClick={() => save('Active')} disabled={saving} className="text-sm font-semibold bg-red-600 hover:bg-red-700 text-white rounded-md px-5 py-2 disabled:opacity-50" data-testid="form-save-activate-btn">
              {saving ? 'Saving…' : 'Save & Activate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierForm;
